import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaBox, FaEdit, FaShoppingCart, FaFilter, FaTimes, FaFileDownload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { productService } from '../../services/product.service';
import * as cartService from '../../services/cart.service';
import { useAuth } from '../../context/AuthContext';
import './Catalog.css';

const Catalog = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [addingToCart, setAddingToCart] = useState(null);
  const [message, setMessage] = useState('');
  const observerTarget = useRef(null);
  const currentPageRef = useRef(1);
  const navigate = useNavigate();

  // Filters state
  const [filters, setFilters] = useState({
    category: [],
    productName: [],
    finish: [],
    color: [],
    suitableFor: [],
    material: [],
    collection: [],
    packType: [],
  });

  // Available filter options
  const [filterOptions, setFilterOptions] = useState({
    category: [],
    productName: [],
    finish: [],
    color: [],
    suitableFor: [],
    material: [],
    collection: [],
    packType: [],
  });

  // Load filter options and cart on mount
  useEffect(() => {
    loadFilterOptions();
    if (user) {
      loadCartCount();
    } else {
      // Load from localStorage if not authenticated
      loadCartCountLocal();
    }
  }, [user]);

  // Extract unique filter options from API products
  const loadFilterOptions = async () => {
    try {
      const options = await productService.getFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  // Load products from API
  const loadProducts = useCallback(async (pageNum, shouldAppend = false) => {
    setLoading(true);
    try {
      // Prepare filters for API
      const apiFilters = {
        page: pageNum,
        limit: 12,
        search: searchTerm,
      };

      // Map filter keys for API call
      if (filters.category.length > 0) apiFilters.category = filters.category;
      if (filters.productName.length > 0) apiFilters.productName = filters.productName;
      if (filters.finish.length > 0) apiFilters.finish = filters.finish;
      if (filters.color.length > 0) apiFilters.color = filters.color;
      if (filters.suitableFor.length > 0) apiFilters.suitableFor = filters.suitableFor;
      if (filters.material.length > 0) apiFilters.material = filters.material;
      if (filters.collection.length > 0) apiFilters.collection = filters.collection;
      if (filters.packType.length > 0) apiFilters.packType = filters.packType;

      // Fetch from API
      const response = await productService.getCatalogProducts(apiFilters);
      
      // Extract products and check has_more flag
      const paginatedProducts = response?.data || [];
      const hasMoreProducts = response?.has_more || false;

      if (!Array.isArray(paginatedProducts)) {
        console.error('Expected products array, got:', paginatedProducts);
        setProducts(shouldAppend ? [] : []);
        setHasMore(false);
        return;
      }

      if (shouldAppend) {
        setProducts((prev) => [...prev, ...paginatedProducts]);
      } else {
        setProducts(paginatedProducts);
      }

      setHasMore(hasMoreProducts);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters]);

  // Load initial products
  useEffect(() => {
    currentPageRef.current = 1;
    loadProducts(1, false);
  }, [searchTerm, filters, loadProducts]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const nextPage = currentPageRef.current + 1;
          currentPageRef.current = nextPage;
          loadProducts(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadProducts]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => {
      const currentValues = prev[filterName] || [];
      if (currentValues.includes(value)) {
        return {
          ...prev,
          [filterName]: currentValues.filter((v) => v !== value),
        };
      } else {
        return {
          ...prev,
          [filterName]: [...currentValues, value],
        };
      }
    });
    currentPageRef.current = 1;
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      category: [],
      productName: [],
      finish: [],
      color: [],
      suitableFor: [],
      material: [],
      collection: [],
      packType: [],
    });
    setSearchTerm('');
    currentPageRef.current = 1;
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  // Mock edit handler
  const handleEdit = (productId) => {
    navigate(`/dashboard/product/edit-product/${productId}`);
  };

  // Load cart count from API
  const loadCartCount = async () => {
    try {
      const cart = await cartService.getShoppingCart();
      setCartCount(Array.isArray(cart) ? cart.length : 0);
    } catch (error) {
      console.error('Error loading cart count:', error);
    }
  };

  // Load cart count from localStorage (for non-authenticated users)
  const loadCartCountLocal = () => {
    try {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      setCartCount(cart.length);
    } catch (error) {
      console.error('Error loading cart count:', error);
    }
  };

  // Add to cart handler
  const handleAddToCart = async (product) => {
    if (!user) {
      // Fallback to localStorage for non-authenticated users
      handleAddToCartLocal(product);
      return;
    }

    try {
      setAddingToCart(product.uid);
      setMessage('');
      await cartService.addToCart(product);
      setMessage('Product added to cart successfully');
      await loadCartCount();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 409) {
        setMessage('Product already in cart');
      } else {
        setMessage('Error adding product to cart');
      }
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setAddingToCart(null);
    }
  };

  // Add to cart handler for non-authenticated users (localStorage fallback)
  const handleAddToCartLocal = (product) => {
    try {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      
      // Check if product already exists in cart
      const existingProduct = cart.find((item) => item.uid === product.uid);
      if (existingProduct) {
        setMessage('Product already in cart');
        setTimeout(() => setMessage(''), 3000);
        return;
      }

      // Add product to cart
      cart.push(product);
      localStorage.setItem('cart', JSON.stringify(cart));
      setCartCount(cart.length);
      setMessage('Product added to cart successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setMessage('Error adding product to cart');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  // Navigate to cart
  const handleViewCart = () => {
    navigate('/dashboard/product/cart');
  };

  // Mock tear sheet handler
  const handleTearSheet = (productId) => {
    console.log('Download tear sheet:', productId);
    // TODO: Implement tear sheet download
  };

  const renderFilterSection = (title, filterKey, options) => {
    return (
      <div className="filter-section">
        <h6 className="filter-title">{title}</h6>
        <div className="filter-options">
          {(options || []).length === 0 ? (
            <p className="text-muted small">No options available</p>
          ) : (
            (options || []).map((option) => (
              <label key={option.id} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters[filterKey]?.includes(option.id) || false}
                  onChange={() => handleFilterChange(filterKey, option.id)}
                />
                <span>{option.name}</span>
              </label>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="catalog-container">
      {/* Header */}
      <div className="catalog-header">
        <div className="header-content">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="header-icon">
              <FaBox size={32} />
            </div>
            <div>
              <h1 className="mb-0">Product Catalogue</h1>
              <p className="mb-0 text-muted">Browse our complete product collection</p>
            </div>
            <button
              className="btn btn-cart ms-auto"
              onClick={handleViewCart}
              title="View shopping cart"
            >
              <FaShoppingCart size={20} />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div
          className="alert alert-success"
          role="alert"
          style={{
            margin: "15px",
            padding: "12px",
            borderRadius: "6px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{message}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage('')}
            aria-label="Close"
          />
        </div>
      )}

      {/* Main Content */}
      <div className="catalog-main">
        {/* Sidebar Filters */}
        <aside className={`catalog-sidebar ${showFilters ? 'show' : ''}`}>
          <div className="sidebar-header">
            <h5 className="mb-0">Filters</h5>
            <button
              className="btn-close-filters"
              onClick={() => setShowFilters(false)}
            >
              <FaTimes />
            </button>
          </div>

          {/* Search in filters */}
          <div className="filter-search mb-3">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Filter Sections */}
          {renderFilterSection('Category', 'category', filterOptions.category)}
          {renderFilterSection('Product Name', 'productName', filterOptions.productName)}
          {renderFilterSection('Finish', 'finish', filterOptions.finish)}
          {renderFilterSection('Color', 'color', filterOptions.color)}
          {renderFilterSection('Suitable For', 'suitableFor', filterOptions.suitableFor)}
          {renderFilterSection('Material', 'material', filterOptions.material)}
          {renderFilterSection('Collection', 'collection', filterOptions.collection)}
          {renderFilterSection('Pack Type', 'packType', filterOptions.packType)}

          {/* Clear Filters */}
          {activeFilterCount > 0 && (
            <button className="btn btn-sm btn-outline-danger w-100" onClick={clearFilters}>
              Clear All Filters
            </button>
          )}
        </aside>

        {/* Main Content Area */}
        <section className="catalog-content">
          {/* Top Bar */}
          <div className="catalog-topbar">
            <button
              className="btn-toggle-filters"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className="me-2" />
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </button>

            <div className="results-count">
              Showing {products.length} products
            </div>
          </div>

          {/* Products Grid */}
          <div className="products-grid">
            {products.length === 0 && !loading ? (
              <div className="no-products">
                <FaBox size={48} className="text-muted mb-3" />
                <h5>No products found</h5>
                <p className="text-muted">Try adjusting your filters or search term</p>
                <button className="btn btn-sm btn-primary" onClick={clearFilters}>
                  Clear Filters
                </button>
              </div>
            ) : (
              products.map((product) => (
                <div key={product.uid} className="product-card">
                    {/* Product Image */}
                    <div className="product-image-container">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={product.images[0].path}
                          alt={product.itemNo || 'Product'}
                          className="product-image"
                        />
                      ) : (
                        <div className="product-image-placeholder">
                          <FaBox size={48} className="text-muted" />
                        </div>
                      )}

                      {/* Edit Icon Overlay */}
                      <button
                        className="btn-image-action edit-icon"
                        onClick={() => handleEdit(product.uid)}
                        title="Edit product"
                      >
                        <FaEdit size={16} />
                      </button>
                    </div>

                    {/* Product Info */}
                    <div className="product-info">
                      {/* Item Number */}
                      <div className="product-item-no">
                        <strong>Item No:</strong>
                        <span>{product.itemNo || 'N/A'}</span>
                      </div>

                      {/* Product Details */}
                      <div className="product-details d-flex justify-content-between align-items-center">
                        <p className="product-category">UID: {product.uid || 'N/A'}</p>
                          {product.color && product.color.length > 0 && (
                            <p className="product-specs">
                              <span className="badge bg-secondary">
                                {product.color.length} color{product.color.length > 1 ? 's' : ''}
                            </span>
                          </p>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="product-actions">
                        <button
                          className="btn btn-action btn-add-cart"
                          onClick={() => handleAddToCart(product)}
                          disabled={addingToCart === product.uid}
                          title="Add to cart"
                        >
                          {addingToCart === product.uid ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            <FaShoppingCart />
                          )}
                        </button>

                        <button
                          className="btn btn-action btn-tear-sheet"
                          onClick={() => handleTearSheet(product.uid)}
                          title="Download tear sheet"
                        >
                          <FaFileDownload />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
          </div>

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <div
              ref={observerTarget}
              className="infinite-scroll-trigger"
              style={{ paddingTop: '40px', textAlign: 'center' }}
            >
              {loading && (
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading more products...</span>
                </div>
              )}
            </div>
          )}

          {/* End of Results */}
          {!hasMore && products.length > 0 && (
            <div className="end-of-results">
              <p className="text-muted">No more products to load</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Catalog;
